/**
 * 🎭 Mail Fingerprint Configuration
 * Konfiguriert Header, Encoding und EHLO-Rotation
 */

import React, { useState, useEffect } from 'react';
import { Fingerprint, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const FingerprintConfig = ({ segment, onUpdate }) => {
  const [rotationMode, setRotationMode] = useState('per_slot');
  const [ehloRotation, setEhloRotation] = useState('per_slot');
  const [useMultipart, setUseMultipart] = useState(true);
  const [multipartPercentage, setMultipartPercentage] = useState(70);
  
  const isConfigured = segment.config?.fingerprint?.rotation_mode || false;
  
  useEffect(() => {
    const config = segment.config?.fingerprint || {};
    if (config.rotation_mode) {
      setRotationMode(config.rotation_mode);
      setEhloRotation(config.ehlo_rotation || 'per_slot');
      setUseMultipart(config.use_multipart !== false);
      setMultipartPercentage(config.multipart_percentage || 70);
    }
  }, [segment]);
  
  const handleSave = () => {
    const updated = { ...segment };
    if (!updated.config) updated.config = {};
    updated.config.fingerprint = {
      rotation_mode: rotationMode,
      ehlo_rotation: ehloRotation,
      use_multipart: useMultipart,
      multipart_percentage: multipartPercentage
    };
    
    onUpdate(updated);
    toast.success('Fingerprint-Konfiguration gespeichert');
  };
  
  return (
    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <Fingerprint className="h-4 w-4 mr-2 text-purple-600" />
          🎭 Mail-Fingerprint (Header + Encoding + EHLO)
        </h4>
      </div>
      
      <div className="space-y-3">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-xs text-blue-800 dark:text-blue-200 mb-3">
          <div className="font-semibold mb-1">ℹ️ Was ist Mail-Fingerprint?</div>
          <ul className="space-y-1 text-xs">
            <li>• <strong>Client-Type:</strong> Gmail, Outlook, iPhone Mail, etc.</li>
            <li>• <strong>Header:</strong> X-Mailer, X-Google-Smtp-Source, Thread-Index, etc.</li>
            <li>• <strong>Encoding:</strong> 7bit (iPhone), base64 (Gmail), quoted-printable (Outlook)</li>
            <li>• <strong>EHLO:</strong> Millionen Variationen (IPs, Hostnames, Cloud-Patterns)</li>
          </ul>
        </div>
        
        {/* Header & Encoding Rotation */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
            📧 Header & Encoding Rotation:
          </label>
          <select
            value={rotationMode}
            onChange={(e) => setRotationMode(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
          >
            <option value="per_segment">Pro Segment (alle Slots gleich)</option>
            <option value="per_slot">Pro Slot (jeder Slot anders)</option>
            <option value="per_mail">Pro Mail (jede Mail anders)</option>
          </select>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Bestimmt wie oft Client-Type, Header und Encoding wechseln
          </div>
        </div>
        
        {/* EHLO Rotation */}
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
            🏷️ EHLO/HELO Rotation:
          </label>
          <select
            value={ehloRotation}
            onChange={(e) => setEhloRotation(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800"
          >
            <option value="per_segment">Pro Segment (konsistent)</option>
            <option value="per_slot">Pro Slot (variiert)</option>
            <option value="per_mail">Pro Mail (maximal random)</option>
          </select>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            EHLO-Hostname beim SMTP-Handshake (z.B. mail-192-168-1-1, smtp-eu-west-1a.internal)
          </div>
        </div>
        
        {/* Multipart/Alternative */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer mb-2">
            <input
              type="checkbox"
              checked={useMultipart}
              onChange={(e) => setUseMultipart(e.target.checked)}
              className="h-4 w-4 text-purple-600 border-gray-300 rounded"
            />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-200">
              📧 Multipart/Alternative (Text + HTML)
            </span>
          </label>
          
          {useMultipart && (
            <div>
              <div className="flex items-center space-x-4 mb-1">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={multipartPercentage}
                  onChange={(e) => setMultipartPercentage(parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-xs font-medium text-gray-900 dark:text-gray-100 w-12">
                  {multipartPercentage}%
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {multipartPercentage}% der Mails als multipart/alternative (wie Gmail/Outlook)
              </div>
            </div>
          )}
        </div>
        
        {/* Client-Type Beispiele */}
        <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-xs">
          <div className="font-semibold mb-2">🎯 Automatische Client-Verteilung:</div>
          <div className="space-y-1">
            <div>• 25% Outlook Web (Thread-Index, Accept-Language, etc.)</div>
            <div>• 20% Gmail Web (X-Google-Smtp-Source, X-Gm-Message-State)</div>
            <div>• 18% iPhone Mail (X-Mailer: iPhone Mail, 7bit encoding)</div>
            <div>• 15% Android Gmail (X-Mailer: Android Gmail, base64)</div>
            <div>• 10% Thunderbird (User-Agent: Mozilla Thunderbird)</div>
            <div>• 7% Apple Mail (X-Mailer: Apple Mail)</div>
            <div>• 5% Clean (keine Header)</div>
          </div>
        </div>
        
        <button
          onClick={handleSave}
          className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center justify-center space-x-2"
        >
          <CheckCircle className="h-4 w-4" />
          <span>Fingerprint-Config speichern</span>
        </button>
      </div>
      
      {isConfigured && (
        <div className="mt-3 p-2 bg-purple-50 dark:bg-purple-900/20 rounded text-xs text-purple-800 dark:text-purple-200">
          ✅ Konfiguriert: Header={rotationMode}, EHLO={ehloRotation}
        </div>
      )}
    </div>
  );
};

export default FingerprintConfig;

